'use strict';

// ---------- Conexión ADB directo desde el navegador (WebUSB) ----------
//
// Reemplaza el binario `adb` + el spawn de child_process de la versión de
// escritorio. Habla el protocolo ADB en JS puro sobre WebUSB (misma técnica
// que usa scrcpy nativo, implementada acá por la librería `@yume-chan/adb`,
// la misma que usa el proyecto ya-webadb).
//
// Requiere: navegador basado en Chromium, página servida por HTTPS (WebUSB
// exige "contexto seguro"), y depuración USB autorizada en el teléfono.

import { Adb, AdbDaemonTransport } from '@yume-chan/adb';
import { AdbDaemonWebUsbDeviceManager } from '@yume-chan/adb-daemon-webusb';
import AdbWebCredentialStore from '@yume-chan/adb-credential-web';

const credentialStore = new AdbWebCredentialStore('tevlr2-web');

export function webUsbDisponible() {
  return Boolean(navigator.usb) && Boolean(AdbDaemonWebUsbDeviceManager.BROWSER);
}

export function contextoSeguro() {
  return window.isSecureContext;
}

/** Pide al usuario elegir el teléfono (abre el selector nativo del navegador). */
export async function elegirDispositivo() {
  const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
  if (!manager) throw new Error('Este navegador no soporta WebUSB. Usá Chrome o Edge.');
  const device = await manager.requestDevice();
  if (!device) throw new Error('No se eligió ningún dispositivo.');
  return device;
}

/** Dispositivos ya autorizados antes: permite reconectar sin volver a pedir permiso. */
export async function dispositivosAutorizados() {
  const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
  if (!manager) return [];
  return manager.getDevices();
}

/** A partir de un AdbDaemonWebUsbDevice, arma la conexión ADB autenticada. */
export async function conectar(device) {
  const connection = await device.connect();
  const transport = await AdbDaemonTransport.authenticate({
    serial: device.serial,
    connection,
    credentialStore,
  });
  return new Adb(transport);
}

/**
 * Corre un comando de shell y devuelve stdin/stdout/stderr como streams.
 * Usa el protocolo de shell v2 (stdout y stderr separados) cuando el
 * teléfono lo soporta — lo normal en Android 8+ — y si no, cae al protocolo
 * viejo (stdout y stderr mezclados, sin stdin persistente útil para audio,
 * pero alcanza para comandos sueltos como "input keyevent").
 */
export async function abrirShell(adb, comando) {
  const shellV2 = adb.subprocess.shellProtocol;
  if (shellV2 && shellV2.isSupported) {
    return shellV2.spawn(comando);
  }
  return adb.subprocess.noneProtocol.spawn(comando);
}

/** Comando de una sola pasada, sin necesidad de mantener el proceso vivo. */
export async function shellTexto(adb, comando) {
  const shellV2 = adb.subprocess.shellProtocol;
  if (shellV2 && shellV2.isSupported) {
    const r = await shellV2.spawnWaitText(comando);
    return r.stdout;
  }
  const bytes = await adb.subprocess.noneProtocol.spawnWait(comando);
  return new TextDecoder().decode(bytes);
}

/** Sube un archivo (Uint8Array) al teléfono vía el servicio sync de ADB. */
export async function subirArchivo(adb, bytes, rutaDestino) {
  const sync = await adb.sync();
  try {
    await sync.write({
      filename: rutaDestino,
      file: new ReadableStream({
        start(controller) {
          controller.enqueue(bytes);
          controller.close();
        },
      }),
    });
  } finally {
    await sync.dispose();
  }
}
